/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import {
  Grid,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import classnames from 'classnames';
import {
  format,
  getDate,
  isSameDay,
  isSameMonth,
  isToday
} from 'date-fns';
import {translate} from '@ion/core/src/locale/locale';
import {
  getDays,
  getHours,
  getMeridiem,
  getMinutes,
  resolveDateInput
} from '@ion/core/src/utils/DateUtil';
import {padStart} from '@ion/core/src/utils/StringUtil';
import {CTALink} from '../../components/buttons/CTALink';
import Day from '../../components/daterangepicker/Day';
import {
  Meridiem,
  NavigationAction,
  TimeModificationAction
} from '../../components/daterangepicker/types';
import {
  chunks,
  getDaysInMonth
} from '../../components/daterangepicker/utils';
import {dlsAlt, dlsColor} from '../../theme/theme';
import Header from './Header';

// defines the styles used by the component
const useStyles = makeStyles(theme => ({
  root              : {},
  weekDaysContainer : {
    marginTop      : 10,
    display        : 'flex',
    justifyContent : 'space-around'
  },
  iconTop : {
    '&:hover' : {
      background : 'none'
    },
    transform : 'rotate(90deg)'
  },
  iconBottom : {
    '&:hover' : {
      background : 'none'
    },
    transform : 'rotate(270deg)'
  },
  daysContainer : {
    marginTop    : 15,
    marginBottom : 20
  },
  time : {
    display    : 'flex',
    alignItems : 'center',
    textAlign  : 'center',
    fontFamily : 'Neo Sans Std',
    fontWeight : 400,
    fontSize   : '30px',
    lineHeight : '36px',
    padding    : '20px 0 20px 0'
  },
  meridiem : {
    cursor : 'pointer'
  },
  cta : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      color : dlsColor('caBlue')
    },
    '&:select' : {
      color : dlsColor('caBlue')
    },
    cursor : 'pointer',
    ...dlsAlt('typography', 'cta2')
  },
  timeContainer : {
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'center',
    padding       : '20 16 20 16'
  }
}));

// defines the prop definitions for the component
export interface MonthProps {
  value: Date;
  isDate?: boolean;
  isTime?: boolean;
  isDateTime?: boolean;
  setValue: (date: Date) => void;
  allowManualEntry?: boolean;
  manualInput?: string;
  handleClickAway: (val?: React.MouseEvent<HTMLElement>) => void;
  handlers: {
    onDayClick: (day: Date, hours: number, minutes: number) => void;
    onDayHover: (day: Date) => void;
    onMonthNavigate: (action: number) => void;
    onYearNavigate: (action: number) => void;
    onInputChange?: Function;
    onChangeDate?: Function;
  };
  hideClearButton: boolean;
}

enum Button {
  clear = 'clear',
  cancel = 'cancel',
  okay = 'okay'
};

// defines the legacy input format map...
const LegacyFormatMap = {
  [TimeModificationAction.HourUp] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours !== 12 ? hours + 1 : 1, min, med}
  ),
  [TimeModificationAction.HourDown] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours !== 1 ? hours - 1 : 12, min, med}
  ),
  [TimeModificationAction.MinuteUp] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours, min: min !== 59 ? min + 1 : 0, med}
  ),
  [TimeModificationAction.MinuteDown] : (hours: number, min: number, med: Meridiem) => (
    {hours: hours, min: min !== 0 ? min - 1 : 59, med}
  ),
  [TimeModificationAction.ToggleMeridiem] : (hours: number, min: number, med: Meridiem) => (
    {hours, min, med: med === Meridiem.AM ? Meridiem.PM : Meridiem.AM}
  ),
  undefined : (hours: number, min: number, med: Meridiem) => (
    {hours, min, med}
  )
};

/**
 * @Month
 *
 * Defines the Month component.
 */
export const Month: FC<MonthProps> = (props: MonthProps) => {
  const classes = useStyles();
  const {
    handlers,
    isDate,
    isTime,
    isDateTime,
    allowManualEntry,
    manualInput,
    value: date,
    setValue: setDate,
    handleClickAway,
    hideClearButton = false
  } = props;

  const {
    onMonthNavigate,
    onYearNavigate,
    onDayHover,
    onDayClick,
    onInputChange,
    onChangeDate
  } = handlers;

  const DAYS_OF_WEEK = translate('common:editors.dateTime.days', {returnObjects: true});

  const dateOriginalRef = useRef(date);
  const dateVal = date || resolveDateInput(new Date());

  const [hourVal, setHourVal]     = useState(dateVal.getHours());
  const [minuteVal, setMinuteVal] = useState(dateVal.getMinutes());

  useEffect(() => {
    onDayClick(dateVal, hourVal, minuteVal);
  }, [hourVal, minuteVal]);

  useEffect(() => {
    setHourVal(dateVal.getHours());
    setMinuteVal(dateVal.getMinutes());
  }, [dateVal]);

  const resolveInput = useCallback((manualInput: string) => {
    const resolved = manualInput.split(' ');
    return isTime ? [null, ...resolved] : resolved;
  }, [isTime]);

  const onLegacyDateTimeChange = useCallback((action: TimeModificationAction) => {
    if (onInputChange) {
      const [date, time, md]  = resolveInput(manualInput);
      const [hr, mi]          = time.split(':');
      const {hours, min, med} = LegacyFormatMap[action]?.(parseInt(hr), parseInt(mi), md as Meridiem);
      const updated           = `${date ?? ''} ${hours}:${padStart(min, 0)} ${med}`.trim();
      onInputChange(updated);
    }
  }, [manualInput, onInputChange, resolveInput]);

  const bump = useCallback((what: number, bump: number, limit: number) => {
    const bumped = what + bump;
    return (bumped >= limit)
      ? (bumped - limit)
      : ((bumped < 0)
        ? (bumped + limit)
        : bumped);
  }, []);

  const onDefaultTimeChange = useCallback((action: TimeModificationAction) => {
    let hour   = hourVal;
    let minute = minuteVal;
    switch (action) {
      case TimeModificationAction.HourUp:
        hour = bump(hour, 1, 24);
        break;
      case TimeModificationAction.HourDown:
        hour = bump(hour, -1, 24);
        break;
      case TimeModificationAction.MinuteUp:
        minute = bump(minute, 1, 60);
        if (minute === 0) {
          hour = bump(hour, 1, 24);
        }
        break;
      case TimeModificationAction.MinuteDown:
        minute = bump(minute, -1, 60);
        if (minute === 59) {
          hour = bump(hour, -1, 24);
        }
        break;
      case TimeModificationAction.ToggleMeridiem:
        hour = bump(hour, 12, 24);
        break;
      default :
        break;
    }
    setHourVal(hour);
    setMinuteVal(minute);
  }, [hourVal, minuteVal]);

  const onChangeTime = useCallback((action: TimeModificationAction) => {
    if (allowManualEntry && manualInput) {
      onLegacyDateTimeChange(action);
    } else {
      onDefaultTimeChange(action);
    }
  }, [allowManualEntry, manualInput]);

  const handleClick = useCallback(({target: {id}}) => {
    if (id !== Button.okay) {
      let date = null;
      if (id === Button.cancel) {
        date = dateOriginalRef.current;
        onChangeDate?.(null);
      }
      setDate(date);
      onChangeDate?.(date);
    }
    handleClickAway();
  }, []);

  return (
    <Grid container>
      {(isDate || isDateTime) && (
        <>
          <Header date={dateVal}
            onMonthNavigate={onMonthNavigate}
            onYearNavigate={onYearNavigate}
            onPrevious={function() {
              onMonthNavigate(NavigationAction.Previous);
            }}
            onNext={function() {
              onMonthNavigate(NavigationAction.Next);
            }} />
          <Grid item
            container
            direction='row'
            justifyContent='space-between'
            className={classes.weekDaysContainer}>
            {DAYS_OF_WEEK.map((day) => (
              <Typography color='textSecondary' key={day} variant='caption'>
                {day}
              </Typography>
            ))}
          </Grid>
          <Grid item
            container
            direction='column'
            justifyContent='space-between'
            className={classes.daysContainer}>
            {chunks(getDaysInMonth(dateVal), 7).map((week, idx) => (
              <Grid justifyContent='space-between' wrap='nowrap' key={`dateFilter-week-${idx}`} container direction='row'>
                {week.map((day) => {
                  const isSelectedDay = !allowManualEntry ? isSameDay(day, dateVal) : getDays(dateVal) === format(day, 'dd');
                  const formatted = `${format(day, 'MM-dd-yyyy')} ${getHours(dateVal, 'hh')}:${getMinutes(dateVal, 'mm')} ${getMeridiem(dateVal)}`;
                  const resolved  = allowManualEntry ? resolveDateInput(formatted) : day;
                  return (
                    <Day key={format(day, 'MM-dd-yyyy')}
                      fromDateTime
                      outlined={isToday(day)}
                      filled={isSelectedDay}
                      disabled={
                        !isSameMonth(dateVal, day)
                      }
                      onClick={function() {
                        onDayClick(resolved, hourVal, minuteVal);
                      }}
                      onHover={function() { onDayHover(day); }}
                      value={getDate(day)} />
                  );
                })}
              </Grid>
            ))}
          </Grid>
        </>)}
      {(isTime || isDateTime) && (
        <Grid item
          container
          direction='row' style={{marginBottom: '20px'}}
          justifyContent='space-between'>
          <div className={classes.time} />
          <div className={classes.timeContainer}>
            <IconButton className={classes.iconTop}
              onClick={function() { onChangeTime(TimeModificationAction.HourUp); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
            <span className={classes.time}>
              {getHours(dateVal, 'h')}
            </span>
            <IconButton className={classes.iconBottom}
              onClick={function() { onChangeTime(TimeModificationAction.HourDown); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
          </div>
          <div className={classes.time}>:</div>
          <div className={classes.timeContainer}>
            <IconButton className={classes.iconTop}
              onClick={function() { onChangeTime(TimeModificationAction.MinuteUp); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
            <span className={classes.time}>
              {getMinutes(dateVal, 'mm')}
            </span>
            <IconButton className={classes.iconBottom}
              onClick={function() { onChangeTime(TimeModificationAction.MinuteDown); }}
              size='small'>
              <ChevronLeft />
            </IconButton>
          </div>
          <div className={classes.time} />
          <div className={classes.timeContainer} style={{justifyContent: 'center'}}>
            <span className={classnames(classes.time, classes.meridiem)}
              onClick={function() { onChangeTime(TimeModificationAction.ToggleMeridiem); }}>
              {getMeridiem(dateVal)}
            </span>
          </div>
          <div className={classes.time} />
        </Grid>)}
      <Grid item
        container
        direction='row'
        justifyContent='flex-end'>
        {!hideClearButton && <CTALink id={Button.clear} className={classes.cta} style={{marginRight: '25px'}} onClick={handleClick}>
          {translate('common:editors.dateTime.clear')}
        </CTALink>}
        <CTALink id={Button.cancel} className={classes.cta} style={{marginRight: '25px'}} onClick={handleClick}>
          {translate('common:editors.dateTime.cancel')}
        </CTALink>
        <CTALink id={Button.okay} className={classes.cta} onClick={handleClick}>
          {translate('common:editors.dateTime.okay')}
        </CTALink>
      </Grid>
    </Grid>
  );
};

export default Month;
